// @ts-nocheck — ported 1:1 from React Native provisioning-cli; runtime-validated
/**
 * ZeroConf Wizard Store - Refactored
 * 
 * Organization:
 * - Group 1: Hardware/Base (Vendor, Model, Hostname, DNS, Timezone)
 * - Group 2: Topology (WAN, LACP, VLANs, Management Network)
 * - Group 3: Services/Policies (Policies, UTM, Cloud Services Mapping)
 * - Group 4: Switch Configurator (Switch Vendor/Model, Port-VLAN Matrix)
 * 
 * Export strategy: Single unified store with all state and actions,
 * maintaining full backward compatibility with existing components.
 */

import { create } from 'zustand';
import { toast } from 'sonner';
import { 
  HardwareModel, 
  getModelByName, 
  getAvailablePorts, 
  getLanPorts, 
  getWanPorts,
  getDefaultWanPort,
  getDefaultLanPort,
  hasHardwareSwitch,
  getHardwareSwitchName,
} from '../hardware/hardwareDictionary';

// Re-export HardwareModel type for use in UI components
export type { HardwareModel } from '../hardware/hardwareDictionary';

// ============================================
// TYPE DEFINITIONS - Group 1: Hardware/Base
// ============================================

/**
 * Supported Firewall Vendors
 */
export type VendorType = 'fortinet' | 'zyxel' | 'zyxel_uos' | 'watchguard' | 'pfsense';

/**
 * pfSense Custom Hardware Interface Configuration
 * For custom-built or VM installations with non-standard NICs
 */
interface PfSenseHardwareConfig {
  interfacePrefix: string;    // Driver prefix: igb, em, ix, vtnet, vmx, hn
  portCount: number;          // Number of ports: 2-16
  customPorts: string[];      // Generated port names: ['igb0', 'igb1', ...]
}

// ============================================
// TYPE DEFINITIONS - Group 2: Topology
// ============================================

/**
 * VLAN Configuration
 * Complete VLAN definition including DHCP and port assignments
 */
export interface VLAN {
  nome_vlan: string;
  vlan_id: string;
  network_ip: string;
  gateway_ip: string;
  subnet_mask: string;
  enable_dhcp: boolean;
  dhcp_start?: string;
  dhcp_end?: string;
  dns_primary?: string;
  dns_secondary?: string;
  porte_assegnate: string[];
  vlan_type: 'corporate' | 'guest' | 'server' | 'management' | 'iot';
}

/**
 * LACP Aggregate (Link Aggregation Control Protocol)
 * Bond configuration for trunk interfaces
 */
export interface LACPAggregate {
  name: string;
  member_ports: string[];
  // Optional: Native (untagged) IP on the aggregate interface
  has_native_ip?: boolean;
  native_ip?: string;
  native_subnet?: string;
}

/**
 * WAN Interface Configuration
 * Supports DHCP, Static IP, PPPoE, and tagged VLAN WANs
 */
export interface WANConfig {
  enabled: boolean;
  type: 'dhcp' | 'static' | 'pppoe';
  physical_port: string;
  is_vlan: boolean;           // WAN as tagged VLAN on trunk
  vlan_id?: string;           // VLAN ID if is_vlan is true
  ip?: string;
  gateway?: string;
  subnet?: string;
  username?: string;          // PPPoE username
  password?: string;          // PPPoE password
}

/**
 * Native Management Network Configuration
 * Untagged network that can be assigned to:
 * - An LACP aggregate trunk interface
 * - A standalone physical port (for single-port deployments)
 */
export interface ManagementNetworkConfig {
  enabled: boolean;
  ipAddress: string;
  subnetMask: string;
  dhcpEnabled: boolean;
  dhcpStart?: string;
  dhcpEnd?: string;
  // NEW: Interface assignment - can be 'Aggregate', a physical port name, or null
  interface: string | null;
}

// ============================================
// TYPE DEFINITIONS - Group 3: Services/Policies
// ============================================

/**
 * WebFilter/UTM Configuration
 * Content filtering categories
 */
export interface WebFilterConfig {
  block_adult: boolean;
  block_malicious: boolean;
  block_social: boolean;
  block_gambling: boolean;
  block_proxy: boolean;
}

/**
 * Outbound Services Configuration
 * Allowed outbound protocols/services
 */
export interface OutboundServicesConfig {
  http: boolean;
  https: boolean;
  dns: boolean;
  imaps: boolean;
  smtps: boolean;
  ftp: boolean;
  ntp: boolean;
  iot_cloud: boolean;         // IoT & Smart Home Cloud (Zero-Trust)
}

/**
 * Policy Configuration
 * Inter-VLAN policy settings
 */
interface PolicyConfig {
  ad_source_vlan?: string;    // Source VLAN for AD policy
  ad_dest_vlan?: string;      // Destination VLAN for AD policy
  guest_vlan?: string;        // Guest isolation VLAN
  corporate_vlans?: string[]; // Corporate VLANs list
}

/**
 * Cloud/SaaS Application Services Mapping
 * Maps service keys to arrays of VLAN IDs that can access them
 */
export type CloudServicesMappingConfig = Record<string, string[]>;

/**
 * Smart Policy Builder Configuration
 * 1-Click Presets for common SMB firewall deployment scenarios
 * 
 * These policies are UI-driven toggles that will be consumed by
 * generator engines in future iterations.
 */
export interface SmartPoliciesConfig {
  // === Business Allowances ===
  /** Allow Staff VLAN to access Printer VLAN (Port 9100, IPP, LPD) */
  policy_allow_printers: boolean;
  /** Allow mDNS/Bonjour/AirPlay/Chromecast discovery across VLANs */
  policy_allow_casting: boolean;
  /** Allow Admin VLAN full unrestricted access to all subnets */
  policy_admin_omnipresence: boolean;
  /** Dedicated SIP/RTP outbound rules for VoIP phones (5060-5061, 10000-20000) */
  policy_voip_outbound: boolean;
  /** Allow Vendor VPN subnet to access HVAC/OT/BMS network */
  policy_vendor_ot: boolean;
  
  // === Zero-Trust Restrictions ===
  /** Strict egress filter: Block outbound SMB(445), RDP(3389), SQL(1433) */
  policy_block_high_risk: boolean;
  /** Layer 3 isolation: Drop inter-client communication on Guest VLAN */
  policy_isolate_guest: boolean;
  
  // === VLAN Mappings for Policies ===
  /** VLAN IDs designated as "Staff" for printer access */
  staff_vlans?: string[];
  /** VLAN ID for Printer network */
  printer_vlan?: string;
  /** VLAN ID for Admin network */
  admin_vlan?: string;
  /** VLAN ID for VoIP network */
  voip_vlan?: string;
  /** VLAN ID for Vendor/VPN network */
  vendor_vlan?: string;
  /** VLAN ID for OT/HVAC/BMS network */
  ot_vlan?: string;
  /** VLAN ID for Guest network */
  guest_vlan?: string;
}

// ============================================
// APP REACHABILITY - L7/FQDN/ISDB Configuration
// ============================================

/**
 * App definition with FQDN and ISDB mappings
 */
export interface AppDefinition {
  id: string;
  name: string;
  icon: string;
  color: string;
  /** Fortinet ISDB names for internet-service routing */
  fortinetIsdb: string[];
  /** Wildcard FQDNs for Zyxel/WatchGuard/pfSense */
  fqdns: string[];
}

/**
 * App reachability rule - which VLANs can/cannot access the app
 * FIX 2: Refactored for dual-action (mutually exclusive arrays)
 */
export interface AppReachabilityRule {
  appId: string;
  /** VLANs where this app is BLOCKED */
  blocked_vlans: string[];
  /** VLANs where this app is ALLOWED (explicit allow) */
  allowed_vlans: string[];
}

/**
 * Complete App Reachability Configuration
 */
export interface AppReachabilityConfig {
  enabled: boolean;
  rules: AppReachabilityRule[];
}

/**
 * Hardcoded App Definitions with FQDN/ISDB Mappings
 * CRITICAL: These values must not be changed - they are vendor-specific identifiers
 */
export const APP_DEFINITIONS: AppDefinition[] = [
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: 'whatsapp',
    color: '#25D366',
    fortinetIsdb: ['WhatsApp-WhatsApp'],
    fqdns: ['*.whatsapp.com', '*.whatsapp.net', '*.wa.me'],
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: 'music-note',
    color: '#000000',
    fortinetIsdb: ['TikTok-TikTok'],
    fqdns: ['*.tiktok.com', '*.tiktokv.com', '*.tiktokcdn.com'],
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: 'instagram',
    color: '#E4405F',
    fortinetIsdb: ['Instagram-Instagram'],
    fqdns: ['*.instagram.com', '*.cdninstagram.com'],
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: 'facebook',
    color: '#1877F2',
    fortinetIsdb: ['Facebook-Facebook', 'Facebook-Web'],
    fqdns: ['*.facebook.com', '*.fbcdn.net'],
  },
  {
    id: 'netflix',
    name: 'Netflix',
    icon: 'netflix',
    color: '#E50914',
    fortinetIsdb: ['Netflix-Netflix'],
    fqdns: ['*.netflix.com', '*.nflxvideo.net', '*.nflxext.com'],
  },
  {
    id: 'spotify',
    name: 'Spotify',
    icon: 'spotify',
    color: '#1DB954',
    fortinetIsdb: ['Spotify-Spotify'],
    fqdns: ['*.spotify.com', '*.scdn.co'],
  },
];

/**
 * Get app definition by ID
 */
export function getAppDefinition(appId: string): AppDefinition | undefined {
  return APP_DEFINITIONS.find(app => app.id === appId);
}

// ============================================
// TYPE DEFINITIONS - Group 4: Switch Configurator
// ============================================

/**
 * Switch Port-VLAN Matrix Configuration
 * Maps port IDs to arrays of VLAN IDs assigned to each port
 */
type SwitchPortVlansMap = Record<string, string[]>;

// ============================================
// INITIAL VALUES
// ============================================

const initialWANConfig: WANConfig = {
  enabled: false,
  type: 'dhcp',
  physical_port: '',
  is_vlan: false,
  vlan_id: '',
};

const initialManagementNetwork: ManagementNetworkConfig = {
  enabled: false,
  ipAddress: '172.16.99.1',  // FIX 2: Management Network on 172.16.99.x (separate from Native 172.16.1.x)
  subnetMask: '255.255.255.0',
  dhcpEnabled: true,
  dhcpStart: '172.16.99.100',
  dhcpEnd: '172.16.99.200',
  interface: null,  // NEW: Can be 'Aggregate', 'port1', 'lan1', etc.
};

const initialWebFilterConfig: WebFilterConfig = {
  block_adult: true,
  block_malicious: true,
  block_social: false,
  block_gambling: true,
  block_proxy: true,
};

const initialOutboundServices: OutboundServicesConfig = {
  http: true,
  https: true,
  dns: true,
  imaps: false,
  smtps: false,
  ftp: false,
  ntp: false,
  iot_cloud: false,
};

const initialSmartPolicies: SmartPoliciesConfig = {
  // Business Allowances - Default OFF (opt-in)
  policy_allow_printers: false,
  policy_allow_casting: false,
  policy_admin_omnipresence: false,
  policy_voip_outbound: false,
  policy_vendor_ot: false,
  // Zero-Trust Restrictions - Default ON (security-first)
  policy_block_high_risk: true,
  policy_isolate_guest: true,
};

// ============================================
// GLOBAL VLAN ID COLLISION RESOLVER
// ============================================
// Prevents VLAN ID conflicts between WAN interfaces and LAN VLANs

interface CollisionCheckContext {
  wan1VlanId?: string | number;
  wan2VlanId?: string | number;
  existingVlans: VLAN[];
}

/**
 * Checks if a VLAN ID is already in use
 * @param vlanId - The VLAN ID to check
 * @param context - Context containing WAN and existing VLAN info
 * @returns true if collision detected, false if free
 */
function isVlanIdInUse(vlanId: number, context: CollisionCheckContext): boolean {
  // Check WAN1 VLAN
  if (context.wan1VlanId) {
    const wan1Id = typeof context.wan1VlanId === 'string' 
      ? parseInt(context.wan1VlanId, 10) 
      : context.wan1VlanId;
    if (!isNaN(wan1Id) && wan1Id === vlanId) {
      console.log(`[CollisionResolver] VLAN ${vlanId} conflicts with WAN1`);
      return true;
    }
  }
  
  // Check WAN2 VLAN
  if (context.wan2VlanId) {
    const wan2Id = typeof context.wan2VlanId === 'string' 
      ? parseInt(context.wan2VlanId, 10) 
      : context.wan2VlanId;
    if (!isNaN(wan2Id) && wan2Id === vlanId) {
      console.log(`[CollisionResolver] VLAN ${vlanId} conflicts with WAN2`);
      return true;
    }
  }
  
  // Check existing VLANs
  for (const vlan of context.existingVlans) {
    if (Number(vlan.vlan_id) === vlanId) {
      console.log(`[CollisionResolver] VLAN ${vlanId} conflicts with existing VLAN "${vlan.nome_vlan}"`);
      return true;
    }
  }
  
  return false;
}

/**
 * Finds the next available VLAN ID starting from the desired ID
 * Uses +1 increment strategy until a free ID is found
 * @param desiredVlanId - The originally requested VLAN ID
 * @param context - Context containing WAN and existing VLAN info
 * @param maxIterations - Safety limit to prevent infinite loops
 * @returns The next available VLAN ID
 */
function findNextAvailableVlanId(
  desiredVlanId: number, 
  context: CollisionCheckContext,
  maxIterations: number = 100
): number {
  let candidateId = desiredVlanId;
  let iterations = 0;
  
  while (isVlanIdInUse(candidateId, context) && iterations < maxIterations) {
    candidateId += 1;
    iterations++;
    
    // Skip reserved VLAN IDs
    if (candidateId === 1 || candidateId === 4095) {
      candidateId += 1;
    }
  }
  
  if (iterations > 0) {
    console.log(`[CollisionResolver] Auto-resolved: VLAN ${desiredVlanId} -> ${candidateId} (after ${iterations} iterations)`);
  }
  
  return candidateId;
}

/**
 * Recalculates IP addresses based on the new VLAN ID using the Third Octet Rule
 * 172.16.{VLAN_ID}.X -> Gateway .1, DHCP .100-.200
 * @param vlan - The VLAN object to update
 * @param newVlanId - The new VLAN ID after collision resolution
 * @returns Updated VLAN with recalculated IPs
 */
function recalculateVlanIpAddresses(vlan: VLAN, newVlanId: number): VLAN {
  const originalVlanId = vlan.vlan_id;
  
  // Only recalculate if VLAN ID actually changed
  if (originalVlanId === newVlanId) {
    return vlan;
  }
  
  // Extract the base Class B prefix (e.g., "172.16")
  const gatewayParts = vlan.gateway_ip.split('.');
  const basePrefix = gatewayParts.length >= 2 
    ? `${gatewayParts[0]}.${gatewayParts[1]}` 
    : '172.16';
  
  // Check if the third octet matches the original VLAN ID (Opinionated Preset pattern)
  const thirdOctet = parseInt(gatewayParts[2] || '0', 10);
  const isOpinionatedPattern = thirdOctet === originalVlanId;
  
  if (isOpinionatedPattern) {
    // Recalculate using Third Octet = VLAN ID
    const newGateway = `${basePrefix}.${newVlanId}.1`;
    const newNetworkIp = `${basePrefix}.${newVlanId}.0`;
    const newDhcpStart = `${basePrefix}.${newVlanId}.100`;
    const newDhcpEnd = `${basePrefix}.${newVlanId}.200`;
    
    console.log(`[CollisionResolver] IP Recalculation: ${vlan.gateway_ip} -> ${newGateway}`);
    
    return {
      ...vlan,
      vlan_id: newVlanId,
      gateway_ip: newGateway,
      network_ip: newNetworkIp,
      dhcp_start: vlan.enable_dhcp ? newDhcpStart : vlan.dhcp_start,
      dhcp_end: vlan.enable_dhcp ? newDhcpEnd : vlan.dhcp_end,
    };
  } else {
    // Non-opinionated pattern - just update the VLAN ID, keep IPs
    console.log(`[CollisionResolver] Non-standard IP pattern - keeping original IPs, only updating VLAN ID`);
    return {
      ...vlan,
      vlan_id: newVlanId,
    };
  }
}

/**
 * Main collision resolver function
 * Checks for conflicts and auto-resolves with IP recalculation
 * @param newVlan - The VLAN to be added
 * @param state - Current wizard state
 * @returns Resolved VLAN with unique ID and updated IPs
 */
function resolveVlanCollision(newVlan: VLAN, state: WizardState): VLAN {
  const context: CollisionCheckContext = {
    wan1VlanId: state.wan1?.is_vlan ? state.wan1.vlan_id : undefined,
    wan2VlanId: state.wan2?.is_vlan ? state.wan2.vlan_id : undefined,
    existingVlans: state.vlans,
  };
  
  const desiredVlanId = newVlan.vlan_id;
  
  // Check if the desired VLAN ID is already in use
  if (!isVlanIdInUse(desiredVlanId, context)) {
    // No collision - return as-is
    return newVlan;
  }
  
  // Find next available VLAN ID
  const resolvedVlanId = findNextAvailableVlanId(Number(desiredVlanId), context);
  
  // Recalculate IP addresses based on new VLAN ID
  const resolvedVlan = recalculateVlanIpAddresses(newVlan, resolvedVlanId);
  
  return resolvedVlan;
}

// ============================================
// WIZARD STATE INTERFACE
// ============================================

interface WizardState {
  // --- Navigation ---
  currentStep: number;
  tenant_id: string;
  config_id?: string;
  
  // --- Group 1: Hardware/Base ---
  vendor: VendorType;
  modello: string;
  hostname: string;
  password_admin: string;
  timezone_id: string;
  mgmt_port: string;
  mgmt_ip: string;
  mgmt_subnet: string;
  dns_primary: string;
  dns_secondary: string;
  
  // HAL: Active Hardware Model (from hardwareDictionary.ts)
  activeHardwareModel: HardwareModel | null;
  
  // pfSense Custom Hardware
  pfsense_interface_prefix: string;
  pfsense_port_count: number;
  pfsense_custom_ports: string[];
  
  // Greenfield/Factory Reset Options
  destroy_switch: boolean;
  remove_dhcp: boolean;
  disable_fortilink: boolean;
  
  // --- Group 2: Topology ---
  wan1: WANConfig;
  wan2: WANConfig;
  // FIX 1: WAN VLAN Collision Validation Errors (Backward Check)
  wan1_vlan_error: string | null;
  wan2_vlan_error: string | null;
  lacp_aggregates: LACPAggregate[];
  vlans: VLAN[];
  managementNetwork: ManagementNetworkConfig;
  
  // Legacy WAN fields (backward compatibility)
  include_sdwan: boolean;
  wan1_type: 'dhcp' | 'static' | 'pppoe';
  wan1_ip?: string;
  wan1_gateway?: string;
  wan1_subnet?: string;
  wan1_username?: string;
  wan1_password?: string;
  wan2_enabled: boolean;
  wan2_type?: 'dhcp' | 'static' | 'pppoe';
  wan2_ip?: string;
  wan2_gateway?: string;
  wan2_subnet?: string;
  wan2_username?: string;
  wan2_password?: string;
  
  // --- Group 3: Services/Policies ---
  policies: string[];
  policy_config: PolicyConfig;
  webfilter_config: WebFilterConfig;
  outbound_services: OutboundServicesConfig;
  cloud_services_mapping: CloudServicesMappingConfig;
  smart_policies: SmartPoliciesConfig;
  app_reachability: AppReachabilityConfig;  // NEW: L7/FQDN App Filtering
  
  // --- Group 4: Switch Configurator ---
  switchVendor: string | null;
  switchModel: string | null;
  switchLacpPorts: string[];
  switchPortVlans: SwitchPortVlansMap;
  
  // --- Output ---
  generated_script: string;
  
  // ============================================
  // ACTIONS - Navigation
  // ============================================
  setStep: (step: number) => void;
  setTenantId: (id: string) => void;
  setConfigId: (id: string) => void;
  
  // ============================================
  // ACTIONS - Group 1: Hardware/Base
  // ============================================
  setVendor: (vendor: VendorType) => void;
  updateStep1: (data: Partial<Pick<WizardState, 
    'vendor' | 'modello' | 'hostname' | 'password_admin' | 
    'timezone_id' | 'mgmt_port' | 'mgmt_ip' | 'mgmt_subnet' | 
    'dns_primary' | 'dns_secondary'
  >>) => void;
  updateStep2: (data: Partial<Pick<WizardState, 
    'destroy_switch' | 'remove_dhcp' | 'disable_fortilink'
  >>) => void;
  
  // pfSense Custom Hardware
  setPfSenseInterfacePrefix: (prefix: string) => void;
  setPfSensePortCount: (count: number) => void;
  generatePfSensePorts: () => void;
  
  // ============================================
  // ACTIONS - Group 2: Topology
  // ============================================
  updateStep3: (data: Partial<WizardState>) => void;
  setWAN1: (config: Partial<WANConfig>) => void;
  setWAN2: (config: Partial<WANConfig>) => void;
  updateStep4: (vlans: VLAN[]) => void;
  addVlan: (newVlan: VLAN) => void;  // FIX 2c: Safe single VLAN add
  setLACPAggregates: (aggregates: LACPAggregate[]) => void;
  setManagementNetwork: (config: Partial<ManagementNetworkConfig>) => void;
  
  // FIX 1: WAN VLAN Collision Validation (Backward Check)
  validateWanVlan: (wanIndex: 1 | 2, vlanId: string) => string | null;
  setWanVlanError: (wanIndex: 1 | 2, error: string | null) => void;
  
  // ============================================
  // ACTIONS - Group 3: Services/Policies
  // ============================================
  updateStep5: (policies: string[]) => void;
  setPolicyConfig: (config: Partial<PolicyConfig>) => void;
  setWebFilterConfig: (config: Partial<WebFilterConfig>) => void;
  setOutboundServices: (config: Partial<OutboundServicesConfig>) => void;
  setCloudServiceMapping: (serviceName: string, vlanIds: string[]) => void;
  setSmartPolicies: (config: Partial<SmartPoliciesConfig>) => void;
  toggleSmartPolicy: (policyKey: keyof SmartPoliciesConfig, value: boolean) => void;
  
  // App Reachability (L7/FQDN/ISDB)
  setAppReachabilityEnabled: (enabled: boolean) => void;
  addAppReachabilityRule: (rule: AppReachabilityRule) => void;
  removeAppReachabilityRule: (appId: string) => void;
  updateAppReachabilityRule: (appId: string, updates: Partial<AppReachabilityRule>) => void;
  
  // ============================================
  // ACTIONS - Group 4: Switch Configurator
  // ============================================
  setSwitchVendor: (vendor: string | null) => void;
  setSwitchModel: (model: string | null) => void;
  setSwitchLacpPorts: (ports: string[]) => void;
  setSwitchPortVlans: (portId: string, vlanIds: string[]) => void;
  resetSwitchConfig: () => void;
  
  // ============================================
  // ACTIONS - Output & Utilities
  // ============================================
  setGeneratedScript: (script: string) => void;
  resetWizard: () => void;
  loadSavedConfig: (savedData: any) => void;  // NEW: Full hydration for edit mode
  loadFromSaved: (payload: any) => void;
  duplicateConfig: (payload: any) => void;
  getPayload: () => any;
  getUsedPorts: () => { wan: string[]; lacp: string[]; all: string[] };
}

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
  // Navigation
  currentStep: 1,
  tenant_id: '',
  
  // Group 1: Hardware/Base
  vendor: 'fortinet' as VendorType,
  modello: '',
  hostname: '',
  password_admin: '',
  timezone_id: '28',
  mgmt_port: '',
  mgmt_ip: '172.16.99.1',  // FIX 2: Management IP on dedicated 172.16.99.x subnet
  mgmt_subnet: '255.255.255.0',
  dns_primary: '8.8.8.8',
  dns_secondary: '8.8.4.4',
  
  // pfSense Custom Hardware
  pfsense_interface_prefix: 'igb',
  pfsense_port_count: 4,
  pfsense_custom_ports: [] as string[],
  
  // HAL: Active Hardware Model (null until model is selected)
  activeHardwareModel: null as HardwareModel | null,
  
  // Greenfield Options
  destroy_switch: true,
  remove_dhcp: true,
  disable_fortilink: true,
  
  // Group 2: Topology
  wan1: { ...initialWANConfig, enabled: true },
  wan2: { ...initialWANConfig },
  // FIX 1: WAN VLAN Collision Validation Errors
  wan1_vlan_error: null as string | null,
  wan2_vlan_error: null as string | null,
  lacp_aggregates: [] as LACPAggregate[],
  vlans: [] as VLAN[],
  managementNetwork: { ...initialManagementNetwork },
  
  // Legacy WAN fields
  include_sdwan: false,
  wan1_type: 'dhcp' as const,
  wan2_enabled: false,
  
  // Group 3: Services/Policies
  policies: [] as string[],
  policy_config: {
    corporate_vlans: [] as string[],
  } as PolicyConfig,
  webfilter_config: { ...initialWebFilterConfig },
  outbound_services: { ...initialOutboundServices },
  cloud_services_mapping: {} as CloudServicesMappingConfig,
  smart_policies: { ...initialSmartPolicies },
  app_reachability: {
    enabled: false,
    rules: [],
  } as AppReachabilityConfig,
  
  // Group 4: Switch Configurator
  switchVendor: null as string | null,
  switchModel: null as string | null,
  switchLacpPorts: [] as string[],
  switchPortVlans: {} as SwitchPortVlansMap,
  
  // Output
  generated_script: '',
};

// ============================================
// ZUSTAND STORE
// ============================================

export const useWizardStore = create<WizardState>((set, get) => ({
  ...initialState,
  
  // ============================================
  // NAVIGATION ACTIONS
  // ============================================
  setStep: (step) => set({ currentStep: step }),
  setTenantId: (id) => set({ tenant_id: id }),
  setConfigId: (id) => set({ config_id: id }),
  
  // ============================================
  // GROUP 1: HARDWARE/BASE ACTIONS
  // ============================================
  setVendor: (vendor) => set((state) => {
    // Phase 5 hotfix: when switching to pfSense, auto-populate
    // pfsense_custom_ports from the configured prefix/count so legacy
    // code paths (which read [0]/[1]/[2] directly) don't TypeError.
    const next: any = { vendor, modello: '' };
    if (vendor === 'pfsense') {
      const cur = Array.isArray(state.pfsense_custom_ports) ? state.pfsense_custom_ports : [];
      if (cur.length === 0) {
        const prefix = state.pfsense_interface_prefix || 'igb';
        const count = Math.max(2, state.pfsense_port_count || 4);
        next.pfsense_custom_ports = Array.from({ length: count }, (_, i) => `${prefix}${i}`);
      }
    }
    return next;
  }),
  
  updateStep1: (data) => set((state) => {
    // HAL: If model is being updated, load the hardware profile from dictionary
    let activeHardwareModel = state.activeHardwareModel;
    if (data.modello && data.modello !== state.modello) {
      activeHardwareModel = getModelByName(data.modello) || null;
      console.log(`[HAL] Hardware model loaded: ${data.modello}`, activeHardwareModel ? activeHardwareModel.ports.map(p => p.name) : 'NOT FOUND');
    }
    return { ...state, ...data, activeHardwareModel };
  }),
  
  updateStep2: (data) => set((state) => ({ ...state, ...data })),
  
  setPfSenseInterfacePrefix: (prefix) => set({ pfsense_interface_prefix: prefix }),
  
  setPfSensePortCount: (count) => set({ 
    pfsense_port_count: Math.max(2, Math.min(16, count)) 
  }),
  
  generatePfSensePorts: () => set((state) => {
    const ports = Array.from(
      { length: state.pfsense_port_count }, 
      (_, i) => `${state.pfsense_interface_prefix}${i}`
    );
    return { pfsense_custom_ports: ports };
  }),
  
  // ============================================
  // GROUP 2: TOPOLOGY ACTIONS
  // ============================================
  updateStep3: (data) => set((state) => ({ ...state, ...data })),
  
  setWAN1: (config) => set((state) => ({
    wan1: { ...state.wan1, ...config },
    // Sync legacy fields for backward compatibility
    wan1_type: config.type || state.wan1.type,
    wan1_ip: config.ip || state.wan1.ip,
    wan1_gateway: config.gateway || state.wan1.gateway,
    wan1_subnet: config.subnet || state.wan1.subnet,
    wan1_username: config.username || state.wan1.username,
    wan1_password: config.password || state.wan1.password,
  })),
  
  setWAN2: (config) => set((state) => ({
    wan2: { ...state.wan2, ...config },
    wan2_enabled: config.enabled !== undefined ? config.enabled : state.wan2.enabled,
    wan2_type: config.type || state.wan2.type,
    wan2_ip: config.ip || state.wan2.ip,
    wan2_gateway: config.gateway || state.wan2.gateway,
    wan2_subnet: config.subnet || state.wan2.subnet,
    wan2_username: config.username || state.wan2.username,
    wan2_password: config.password || state.wan2.password,
  })),
  
  // FIX 2c: Safe updateStep4 with deep clone to prevent reference issues
  updateStep4: (vlans) => {
    try {
      // Deep clone the entire array to prevent any reference mutations
      const clonedVlans = JSON.parse(JSON.stringify(vlans));
      return set({ vlans: clonedVlans });
    } catch (error) {
      console.error('[wizardStore] updateStep4 error:', error);
      // Fallback: use spread operator
      return set({ vlans: [...vlans] });
    }
  },
  
  // FIX 2c: Safe addVlan action with deep clone AND collision resolution
  addVlan: (newVlan: VLAN) => set((state) => {
    try {
      // Deep clone the new VLAN to prevent reference issues
      const clonedVlan = JSON.parse(JSON.stringify(newVlan));
      
      // COLLISION RESOLVER: Check for VLAN ID conflicts with WAN and existing VLANs
      const resolvedVlan = resolveVlanCollision(clonedVlan, state);
      
      // Log if collision was resolved AND surface a transient toast so the user
      // sees that we auto-bumped the duplicate id (Phase 5 polish).
      if (resolvedVlan.vlan_id !== clonedVlan.vlan_id) {
        console.log(`[wizardStore] VLAN Collision Resolved: "${newVlan.nome_vlan}" VLAN ${clonedVlan.vlan_id} -> ${resolvedVlan.vlan_id}`);
        console.log(`[wizardStore] New Gateway IP: ${resolvedVlan.gateway_ip}`);
        try {
          toast.success(`VLAN ID ${clonedVlan.vlan_id} was taken — auto-assigned ${resolvedVlan.vlan_id}.`, { duration: 3000 });
        } catch { /* toast might not be mounted in tests */ }
      }
      
      // Deep clone existing array and append the resolved VLAN
      const existingVlans = JSON.parse(JSON.stringify(state.vlans));
      return { vlans: [...existingVlans, resolvedVlan] };
    } catch (error) {
      console.error('[wizardStore] addVlan error:', error);
      // Fallback: use spread operator without collision resolution
      return { vlans: [...state.vlans, { ...newVlan }] };
    }
  }),
  
  setLACPAggregates: (aggregates) => set({ lacp_aggregates: aggregates }),
  
  setManagementNetwork: (config) => set((state) => ({
    managementNetwork: { ...state.managementNetwork, ...config }
  })),
  
  // FIX 1: WAN VLAN Collision Validation (Backward Check - Preset -> WAN)
  validateWanVlan: (wanIndex, vlanId) => {
    const state = get();
    const parsedVlanId = typeof vlanId === 'string' ? parseInt(vlanId, 10) : vlanId;
    
    // If empty or invalid, no error (allow blank)
    if (!vlanId || isNaN(parsedVlanId)) {
      return null;
    }
    
    // Check against existing internal VLANs (Backward Check)
    for (const vlan of state.vlans) {
      if (Number(vlan.vlan_id) === parsedVlanId) {
        const errorMsg = `VLAN ID ${parsedVlanId} is already used by "${vlan.nome_vlan}". Change this ID or remove the conflicting VLAN in Step 4.`;
        console.log(`[WAN Validation] Backward collision detected: WAN${wanIndex} VLAN ${parsedVlanId} conflicts with "${vlan.nome_vlan}"`);
        return errorMsg;
      }
    }
    
    // Check against the OTHER WAN (if both are VLAN-tagged)
    if (wanIndex === 1 && state.wan2?.is_vlan) {
      const wan2VlanId = typeof state.wan2.vlan_id === 'string' 
        ? parseInt(state.wan2.vlan_id, 10) 
        : state.wan2.vlan_id;
      if (!isNaN(wan2VlanId) && wan2VlanId === parsedVlanId) {
        return `VLAN ID ${parsedVlanId} is already used by WAN2.`;
      }
    }
    if (wanIndex === 2 && state.wan1?.is_vlan) {
      const wan1VlanId = typeof state.wan1.vlan_id === 'string' 
        ? parseInt(state.wan1.vlan_id, 10) 
        : state.wan1.vlan_id;
      if (!isNaN(wan1VlanId) && wan1VlanId === parsedVlanId) {
        return `VLAN ID ${parsedVlanId} is already used by WAN1.`;
      }
    }
    
    return null; // No collision
  },
  
  setWanVlanError: (wanIndex, error) => set((state) => ({
    [`wan${wanIndex}_vlan_error`]: error,
  })),
  
  // ============================================
  // GROUP 3: SERVICES/POLICIES ACTIONS
  // ============================================
  updateStep5: (policies) => set({ policies }),
  
  setPolicyConfig: (config) => set((state) => ({ 
    policy_config: { ...state.policy_config, ...config } 
  })),
  
  setWebFilterConfig: (config) => set((state) => ({
    webfilter_config: { ...state.webfilter_config, ...config }
  })),
  
  setOutboundServices: (config) => set((state) => ({
    outbound_services: { ...state.outbound_services, ...config }
  })),
  
  setCloudServiceMapping: (serviceName, vlanIds) => set((state) => ({
    cloud_services_mapping: { 
      ...state.cloud_services_mapping, 
      [serviceName]: vlanIds 
    }
  })),
  
  setSmartPolicies: (config) => set((state) => ({
    smart_policies: { ...state.smart_policies, ...config }
  })),
  
  toggleSmartPolicy: (policyKey, value) => set((state) => ({
    smart_policies: { 
      ...state.smart_policies, 
      [policyKey]: value 
    }
  })),
  
  // App Reachability (L7/FQDN/ISDB) Actions
  setAppReachabilityEnabled: (enabled) => set((state) => ({
    app_reachability: { ...state.app_reachability, enabled }
  })),
  
  addAppReachabilityRule: (rule) => set((state) => {
    // Check if rule for this app already exists
    const existingIndex = state.app_reachability.rules.findIndex(r => r.appId === rule.appId);
    if (existingIndex >= 0) {
      // Replace existing rule
      const newRules = [...state.app_reachability.rules];
      newRules[existingIndex] = rule;
      return { app_reachability: { ...state.app_reachability, rules: newRules } };
    }
    // Add new rule
    return {
      app_reachability: {
        ...state.app_reachability,
        rules: [...state.app_reachability.rules, rule]
      }
    };
  }),
  
  removeAppReachabilityRule: (appId) => set((state) => ({
    app_reachability: {
      ...state.app_reachability,
      rules: state.app_reachability.rules.filter(r => r.appId !== appId)
    }
  })),
  
  updateAppReachabilityRule: (appId, updates) => set((state) => ({
    app_reachability: {
      ...state.app_reachability,
      rules: state.app_reachability.rules.map(r =>
        r.appId === appId ? { ...r, ...updates } : r
      )
    }
  })),
  
  // ============================================
  // GROUP 4: SWITCH CONFIGURATOR ACTIONS
  // ============================================
  setSwitchVendor: (vendor) => set({ 
    switchVendor: vendor, 
    switchModel: null, 
    switchLacpPorts: [], 
    switchPortVlans: {} 
  }),
  
  setSwitchModel: (model) => set({ 
    switchModel: model, 
    switchLacpPorts: [], 
    switchPortVlans: {} 
  }),
  
  setSwitchLacpPorts: (ports) => set({ switchLacpPorts: ports }),
  
  setSwitchPortVlans: (portId, vlanIds) => set((state) => ({
    switchPortVlans: { 
      ...state.switchPortVlans, 
      [portId]: vlanIds 
    }
  })),
  
  resetSwitchConfig: () => set({ 
    switchVendor: null, 
    switchModel: null, 
    switchLacpPorts: [], 
    switchPortVlans: {} 
  }),
  
  // ============================================
  // OUTPUT & UTILITIES ACTIONS
  // ============================================
  setGeneratedScript: (script) => set({ generated_script: script }),
  
  resetWizard: () => set({ ...initialState }),
  
  /**
   * Load a saved configuration into the wizard for editing
   * CRITICAL: Performs deep clone to prevent reference issues
   * Safely merges saved data with initial state defaults
   */
  loadSavedConfig: (savedData: any) => {
    try {
      // Deep clone to prevent any reference mutations
      const clonedData = JSON.parse(JSON.stringify(savedData));
      
      console.log('[wizardStore] Loading saved config:', clonedData.hostname || 'unknown');
      
      // Merge with initial state to ensure all required fields exist
      const mergedState = {
        ...initialState,
        // Group 1: Hardware/Base
        vendor: clonedData.vendor || initialState.vendor,
        modello: clonedData.modello || '',
        hostname: clonedData.hostname || '',
        password_admin: clonedData.password_admin || '',
        timezone_id: clonedData.timezone_id || '28',
        mgmt_port: clonedData.mgmt_port || '',
        mgmt_ip: clonedData.mgmt_ip || '172.16.99.1',
        mgmt_subnet: clonedData.mgmt_subnet || '255.255.255.0',
        dns_primary: clonedData.dns_primary || '8.8.8.8',
        dns_secondary: clonedData.dns_secondary || '8.8.4.4',
        
        // pfSense Custom Hardware
        pfsense_interface_prefix: clonedData.pfsense_interface_prefix || 'igb',
        pfsense_port_count: clonedData.pfsense_port_count || 4,
        pfsense_custom_ports: clonedData.pfsense_custom_ports || [],
        
        // Greenfield Options
        destroy_switch: clonedData.destroy_switch !== undefined ? clonedData.destroy_switch : true,
        remove_dhcp: clonedData.remove_dhcp !== undefined ? clonedData.remove_dhcp : true,
        disable_fortilink: clonedData.disable_fortilink !== undefined ? clonedData.disable_fortilink : true,
        
        // Group 2: Topology - CRITICAL: Arrays must be fully replaced
        wan1: clonedData.wan1 || { ...initialWANConfig, enabled: true },
        wan2: clonedData.wan2 || { ...initialWANConfig },
        lacp_aggregates: Array.isArray(clonedData.lacp_aggregates) ? clonedData.lacp_aggregates : [],
        vlans: Array.isArray(clonedData.vlans) ? clonedData.vlans : [],
        managementNetwork: clonedData.managementNetwork || { ...initialManagementNetwork },
        
        // Legacy WAN fields
        include_sdwan: clonedData.include_sdwan || false,
        wan1_type: clonedData.wan1_type || 'dhcp',
        wan1_ip: clonedData.wan1_ip,
        wan1_gateway: clonedData.wan1_gateway,
        wan1_subnet: clonedData.wan1_subnet,
        wan1_username: clonedData.wan1_username,
        wan1_password: clonedData.wan1_password,
        wan2_enabled: clonedData.wan2_enabled || false,
        wan2_type: clonedData.wan2_type,
        wan2_ip: clonedData.wan2_ip,
        wan2_gateway: clonedData.wan2_gateway,
        wan2_subnet: clonedData.wan2_subnet,
        wan2_username: clonedData.wan2_username,
        wan2_password: clonedData.wan2_password,
        
        // Group 3: Services/Policies - Merge with defaults
        policies: Array.isArray(clonedData.policies) ? clonedData.policies : [],
        policy_config: clonedData.policy_config || { corporate_vlans: [] },
        webfilter_config: { ...initialWebFilterConfig, ...(clonedData.webfilter_config || {}) },
        outbound_services: { ...initialOutboundServices, ...(clonedData.outbound_services || {}) },
        cloud_services_mapping: clonedData.cloud_services_mapping || {},
        smart_policies: { ...initialSmartPolicies, ...(clonedData.smart_policies || {}) },
        app_reachability: clonedData.app_reachability
          ? { enabled: !!clonedData.app_reachability.enabled, rules: Array.isArray(clonedData.app_reachability.rules) ? clonedData.app_reachability.rules : [] }
          : { enabled: false, rules: [] },
        
        // Group 4: Switch Configurator
        switchVendor: clonedData.switchVendor || null,
        switchModel: clonedData.switchModel || null,
        switchLacpPorts: clonedData.switchLacpPorts || [],
        switchPortVlans: clonedData.switchPortVlans || {},
        
        // Preserve IDs for editing
        tenant_id: clonedData.tenant_id || '',
        config_id: clonedData._id || clonedData.config_id || undefined,
        
        // CRITICAL: Start at step 1 for editing
        currentStep: 1,
        
        // Clear generated script (will be regenerated)
        generated_script: '',
      };
      
      return set(mergedState);
    } catch (error) {
      console.error('[wizardStore] loadSavedConfig error:', error);
      // On error, just reset to initial state
      return set({ ...initialState, currentStep: 1 });
    }
  },
  
  loadFromSaved: (payload) => set({ ...payload, currentStep: 1 }),
  
  duplicateConfig: (payload) => set({
    ...payload,
    config_id: undefined,
    tenant_id: '',
    hostname: payload.hostname + '_copy',
    currentStep: 1,
  }),
  
  /**
   * Get ports currently used by WAN and LACP configurations
   * Used to prevent port conflicts in the UI
   */
  getUsedPorts: () => {
    const state = get();
    const wanPorts: string[] = [];
    const lacpPorts: string[] = [];
    
    // WAN ports (access mode only - tagged VLAN WANs don't consume ports)
    if (state.wan1.enabled && state.wan1.physical_port && !state.wan1.is_vlan) {
      wanPorts.push(state.wan1.physical_port);
    }
    if (state.wan2.enabled && state.wan2.physical_port && !state.wan2.is_vlan) {
      wanPorts.push(state.wan2.physical_port);
    }
    
    // LACP member ports
    state.lacp_aggregates.forEach(lacp => {
      lacpPorts.push(...lacp.member_ports);
    });
    
    return {
      wan: wanPorts,
      lacp: lacpPorts,
      all: [...new Set([...wanPorts, ...lacpPorts])],
    };
  },
  
  /**
   * Get serializable payload for API save operations
   */
  getPayload: () => {
    const state = get();
    return {
      tenant_id: state.tenant_id,
      vendor: state.vendor,
      modello: state.modello,
      hostname: state.hostname,
      password_admin: state.password_admin,
      timezone_id: state.timezone_id,
      mgmt_port: state.mgmt_port,
      mgmt_ip: state.mgmt_ip,
      mgmt_subnet: state.mgmt_subnet,
      dns_primary: state.dns_primary,
      dns_secondary: state.dns_secondary,
      destroy_switch: state.destroy_switch,
      remove_dhcp: state.remove_dhcp,
      disable_fortilink: state.disable_fortilink,
      wan1: state.wan1,
      wan2: state.wan2,
      // Legacy fields for backward compatibility
      include_sdwan: state.include_sdwan,
      wan1_type: state.wan1.type,
      wan1_ip: state.wan1.ip,
      wan1_gateway: state.wan1.gateway,
      wan1_subnet: state.wan1.subnet,
      wan1_username: state.wan1.username,
      wan1_password: state.wan1.password,
      wan2_enabled: state.wan2.enabled,
      wan2_type: state.wan2.type,
      wan2_ip: state.wan2.ip,
      wan2_gateway: state.wan2.gateway,
      wan2_subnet: state.wan2.subnet,
      wan2_username: state.wan2.username,
      wan2_password: state.wan2.password,
      lacp_aggregates: state.lacp_aggregates,
      vlans: state.vlans,
      managementNetwork: state.managementNetwork,
      policies: state.policies,
      policy_config: state.policy_config,
      webfilter_config: state.webfilter_config,
      outbound_services: state.outbound_services,
      cloud_services_mapping: state.cloud_services_mapping,
      smart_policies: state.smart_policies,
      app_reachability: state.app_reachability,
    };
  },
}));
